import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { calculateTriangleArea, scaledCoordinate } from '../drawing.helper'

export type XABCDPatternDrawingState = {
    x?: Point,
    a?: Point,
    b?: Point,
    c?: Point,
    d?: Point
}

export type XABCDPatternDrawingSettings = {
    lineColor: ColorValue,
    fillColor: ColorValue,
    lineWidth: number
}

class XABCDPatternDrawingTool extends DrawingBase<any, XABCDPatternDrawingSettings, XABCDPatternDrawingState> {
    scaledCoordinates: Record<string, PointCoordinates> = {}
    lengths: Record<string, number> = {}

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        const { x, a, b, c, d } = this.scaledCoordinates

        let prevPoint: PointCoordinates

        ctx.beginPath()
        this.setLineAttributes(ctx)
        ctx.fillStyle = this._settings.fillColor.color
        const points = [x, a, b, c, d]
        points.forEach((p, i) => {
            if (!p) return

            if (!prevPoint) {
                ctx.moveTo(p.x, p.y)
            } else {
                ctx.lineTo(p.x, p.y)
            }
            prevPoint = p
            ctx.globalAlpha = this._settings.lineColor.opacity
            ctx.stroke()
            if (i === 2 || i === 4) {
                ctx.lineTo(points[i - 2].x, points[i - 2].y)
                ctx.globalAlpha = this._settings.fillColor.opacity
                ctx.fill()
                ctx.closePath()
                ctx.beginPath()
                ctx.moveTo(p.x, p.y)
            }
        })

        this.drawChangeLines(ctx)

    };

    drawChangeLines(ctx: CanvasRenderingContext2D) {
        const { x, a, b, c, d } = this.scaledCoordinates
        ctx.beginPath()
        if (b) {
            ctx.moveTo(x.x, x.y)
            ctx.lineTo(b.x, b.y)
        }

        if (c) {
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(c.x, c.y)
        }

        if (d) {
            ctx.moveTo(b.x, b.y)
            ctx.lineTo(d.x, d.y)
            ctx.moveTo(x.x, x.y)
            ctx.lineTo(d.x, d.y)
        }

        this.setLineAttributes(ctx, true)
        ctx.stroke()
        ctx.closePath()
        this.drawChangeRatios(ctx)
        this.drawPointsLabels(ctx)
    }

    drawPointsLabels(ctx: CanvasRenderingContext2D) {
        const { x, a, b, c, d } = this._state
        const points = [x, a, b, c, d].filter(v => v)

        points.forEach((p, i) => {
            const refPoint = i == 0 ? i + 1 : i - 1

            const position = points[refPoint] && points[refPoint]!.y > p!.y ? -1 : 1
            const renderCoords = this.scaledCoordinates[p!.id]
            const text = p!.id.toUpperCase()
            ctx.font = (12 * window.devicePixelRatio) + 'px sans-serif'
            ctx.textBaseline = 'top'
            ctx.globalAlpha = 1
            const padding = 4 * window.devicePixelRatio
            const dim = ctx.measureText(text)
            const textWidth = dim.width
            const textHeight = dim.actualBoundingBoxAscent + dim.actualBoundingBoxDescent
            const startX = renderCoords.x - textWidth / 2
            const startY = renderCoords.y - (position > 0 ? textHeight + 2 * padding : -2 * padding)
            ctx.fillStyle = this._settings.lineColor.color
            ctx.beginPath()
            ctx.roundRect(startX - padding, startY - padding, textWidth + 2 * padding, textHeight + 2 * padding + 2 * window.devicePixelRatio, 3 * window.devicePixelRatio)
            ctx.fill()
            ctx.closePath()
            ctx.fillStyle = '#fff'
            ctx.fillText(text, startX, startY)
        })
    }

    drawChangeRatios(ctx: CanvasRenderingContext2D) {
        const { xa, ab, bc, cd, ad } = this.lengths
        const { x, a, b, c, d } = this.scaledCoordinates

        if (b) {
            const ratio = Number((ab / xa).toFixed(3))
            this.drawRatioText(ctx, ratio.toString(), x, b)
        }

        if (c) {
            const ratio = Number((bc / ab).toFixed(3))
            this.drawRatioText(ctx, ratio.toString(), a, c)
        }

        if (d) {
            let ratio = Number((cd / bc).toFixed(3))
            this.drawRatioText(ctx, ratio.toString(), b, d)

            ratio = Number((ad / xa).toFixed(3))
            this.drawRatioText(ctx, ratio.toString(), x, d)
        }

    }

    drawRatioText(ctx: CanvasRenderingContext2D, text: string, start: PointCoordinates, end: PointCoordinates) {
        ctx.font = (12 * window.devicePixelRatio) + 'px sans-serif'
        ctx.textBaseline = 'top'
        ctx.globalAlpha = 1
        const dim = ctx.measureText(text)
        const textWidth = dim.width
        const textHeight = dim.actualBoundingBoxAscent + dim.actualBoundingBoxDescent
        const centerX = (start.x + end.x) / 2
        const centerY = (start.y + end.y) / 2
        const startX = centerX - textWidth / 2
        const startY = centerY - textHeight
        const padding = 4 * window.devicePixelRatio
        ctx.fillStyle = this._settings.lineColor.color
        ctx.beginPath()
        ctx.roundRect(startX - padding, startY - padding, textWidth + 2 * padding, textHeight + 2 * padding + 2 * window.devicePixelRatio, 3 * this.verticalPixelRatio)
        ctx.fill()
        ctx.closePath()
        ctx.fillStyle = '#fff'
        ctx.fillText(text, startX, startY)
    }

    calculateLineAttributes() {
        const { x, a, b, c, d } = this._state

        let prevPoint: Point;
        [x, a, b, c, d].forEach((p) => {
            if (!p) return
            let x = this.timeToCoordinate(p.x)
            let y = this.priceToCoordinate(p.y)

            if (x && y) {
                this.scaledCoordinates[p.id] = {
                    x: scaledCoordinate(x, this.horizontalPixelRatio),
                    y: scaledCoordinate(y, this.verticalPixelRatio)
                }
            }

            if (prevPoint) {
                this.lengths[`${prevPoint.id}${p.id}`] = Math.abs(p.y - prevPoint.y)
            }

            prevPoint = p
        })

        if (a && d) {
            this.lengths.ad = Math.abs(a.y - d.y)
        }
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = false) {
        if (setLineType) {
            const dotWidth = Math.max(2, this._settings.lineWidth) * window.devicePixelRatio
            let lineDash: number[] = [2 * dotWidth, dotWidth]
            ctx.setLineDash(lineDash)
        }
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = this._settings.lineWidth * window.devicePixelRatio
        ctx.strokeStyle = this._settings.lineColor.color
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const point: Point = {
            id: 'x',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.x = point
        } else if (this.lastAddedPoint.id == 'x') {
            point.id = 'a'
            this._state.a = point
        } else if (this.lastAddedPoint.id == 'a') {
            point.id = 'b'
            this._state.b = point
        } else if (this.lastAddedPoint.id == 'b') {
            point.id = 'c'
            this._state.c = point
        } else {
            point.id = 'd'
            this._state.d = point
            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const { x, a, b, c, d } = this._state

        return [x!, a!, b!, c!, d!].filter(p => p)
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const { x: xs, a, b, c, d } = this.scaledCoordinates
        if (!xs || !a || !b || !c || !d) return null

        if (this.isWithinTriangle(x, y, xs, a, b) || this.isWithinTriangle(x, y, b, c, d)) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }

        return null
    }

    isWithinTriangle(x: number, y: number, p1: PointCoordinates, p2: PointCoordinates, p3: PointCoordinates) {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const area = calculateTriangleArea(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y)
        const area1 = calculateTriangleArea(p1.x, p1.y, p2.x, p2.y, scaledX, scaledY)
        const area2 = calculateTriangleArea(p2.x, p2.y, p3.x, p3.y, scaledX, scaledY)
        const area3 = calculateTriangleArea(p1.x, p1.y, p3.x, p3.y, scaledX, scaledY)

        return Number(area.toFixed(1)) === Number((area1 + area2 + area3).toFixed(1))
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'x':
                    this._state.x = h
                    break
                case 'a':
                    this._state.a = h
                    break
                case 'b':
                    this._state.b = h
                    break
                case 'c':
                    this._state.c = h
                    break
                case 'd':
                    this._state.d = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        if (!this._state[id as keyof typeof this._state]) return
        this._state[id as keyof typeof this._state]!.x = time
        this._state[id as keyof typeof this._state]!.y = price
    }
    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { x, a, b, c, d } = this._state
        const points = [x, a, b, c, d].filter(v => v)
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => a!.y - b!.y)
        const configs: LabelAxisViewConfig<number>[] = [
            {
                id: 'outer',
                p1: sorted[0]!.y,
                p2: sorted[sorted.length - 1]!.y,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'price-' + sorted[c]!.id,
                p1: sorted[c]!.y,
                p2: sorted[c]!.y,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }
        return configs
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { x, a, b, c, d } = this._state
        const points = [x, a, b, c, d].filter(v => v)
        if (points.length < 2) return []
        if (points.length < 2) return []

        const sorted = [...points].sort((a, b) => (a!.x as number) - (b!.x as number))
        const configs: LabelAxisViewConfig<Time>[] = [
            {
                id: 'time',
                p1: sorted[0]!.x,
                p2: sorted[sorted.length - 1]!.x,
                fill: true,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            }
        ]

        for (let c = 1; c < sorted.length - 1; c++) {
            configs.push({
                id: 'time-' + sorted[c]!.id,
                p1: sorted[c]!.x,
                p2: sorted[c]!.x,
                fill: false,
                fillColor: this._settings.lineColor.color,
                labelColors: {
                    p1: this._settings.lineColor.color,
                    p2: this._settings.lineColor.color
                }
            })
        }

        return configs
    }

    getAvailableSettings = (): DrawingSettingGroup<XABCDPatternDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#2c7bf4',
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'fillColor',
                    label: 'Fill Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#2c7bf4',
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                },

                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 1,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): XABCDPatternDrawingSettings {
        return { ...this._settings }
    }

    getDrawingType(): DrawingTypes {
        return 'xabcdPattern'
    }

    getMiniSettings = () => {
        return ['fillColor', 'lineColor', 'lineWidth'] as (keyof XABCDPatternDrawingSettings)[]
    }
}

export default XABCDPatternDrawingTool